<script setup lang="ts">
/**
 * One company or team KPI: what was set (#895) — its name, target with direction, AMBER band, cadence and
 * source — and, when `readable`, how it is doing (#896): its light, trend and latest reading, its reading
 * history, and the form a person enters a reading with. "No target yet" stands where a KPI has none, and neither
 * it nor a KPI with nothing read yet draws a light: there is nothing to judge.
 *
 * The light, the trend and overdue come from the `@zerohuman/shared` derivation the API and the MCP server use,
 * so a KPI reads the same here as on the board. Used on HQ for the company's own KPIs and on a team's page for
 * its own.
 *
 * A KPI still waiting for a person's sign-off says so in words, at the top of the card, with what that means: no run
 * sees it or records a reading for it until then. It draws no light and offers no reading form, since the API takes
 * no reading for it.
 */
import { kpiLight, kpiOverdue, kpiTrend, sortReadingsNewestFirst } from '@zerohuman/shared'
import { Button, ZhKpiLight, ZhSystemLabel } from '@zerohuman/ui'
import { computed, onMounted, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import { api, type KpiReadingRecord } from '@/api'
import { type Kpi, kpiBandText, kpiCadenceText, kpiPending, kpiTargetOf, kpiTargetText } from '@/lib/kpi'

const props = defineProps<{
    kpi: Kpi
    /** The source's name, when the page knows it. Without one the source is shown as the id it is stored as. */
    sourceName?: string | null
    /** Read this KPI's readings, show how it is doing, and offer the form to enter one. */
    readable?: boolean
    /**
     * This KPI's readings, when a list of cards reads them for all its cards at once (KpiTargetCards.vue, issue #556):
     * a card handed them never reads its own, and after a reading is recorded it leaves the list to read them again.
     * `null` is a read that has not answered or failed. Left out, a `readable` card reads its own readings.
     */
    readings?: KpiReadingRecord[] | null
}>()
const emit = defineEmits<{ recorded: [] }>()

const target = computed(() => kpiTargetText(props.kpi))
const band = computed(() => kpiBandText(props.kpi))
const cadence = computed(() => kpiCadenceText(props.kpi))
const hasTarget = computed(() => props.kpi.targetValue !== null)
const pending = computed(() => kpiPending(props.kpi))

/** Who or what the readings come from, by name where it is known. A task links to its page. */
const source = computed(() => {
    const { sourceKind, sourceRef } = props.kpi
    if (!sourceKind || !sourceRef) return null
    return {
        kind: sourceKind,
        name: props.sourceName ?? sourceRef,
        to: sourceKind === 'task' ? `/tasks/${sourceRef}` : null,
    }
})

/** Whether a list hands this card its readings, so the card never reads them itself. */
const handedReadings = computed(() => props.readings !== undefined)

/** The card's own read, when nobody hands it its readings. */
const ownReadings = ref<KpiReadingRecord[] | null>(null)

/** `null` until read, and when the read fails: a KPI whose readings cannot be read says nothing about them. */
const shownReadings = computed(() => (handedReadings.value ? (props.readings ?? null) : ownReadings.value))

async function loadReadings(): Promise<void> {
    if (!props.readable || handedReadings.value) return
    const kpiId = props.kpi.id
    const loaded = await api.kpiReadings(kpiId).catch(() => null)
    if (kpiId === props.kpi.id) ownReadings.value = loaded
}

onMounted(loadReadings)
watch(() => props.kpi.id, loadReadings)

const ordered = computed(() => sortReadingsNewestFirst(shownReadings.value ?? []))
const latest = computed(() => ordered.value[0] ?? null)
const light = computed(() => (pending.value ? null : kpiLight(kpiTargetOf(props.kpi), ordered.value)))
const overdue = computed(
    () => kpiOverdue(kpiTargetOf(props.kpi), props.kpi.cadenceMinutes, ordered.value, new Date())?.overdue === true,
)

/** The number's own movement against the reading before it. The arrow reports the move; the light judges it. */
const ARROWS = { up: '↑', down: '↓', flat: '→' } as const
const trend = computed(() => {
    const moved = kpiTrend(ordered.value)
    return moved ? `${ARROWS[moved.direction]} ${moved.percent}%` : null
})

function valueText(read: number): string {
    return props.kpi.unit ? `${read} ${props.kpi.unit}` : String(read)
}

function dayText(taken: string): string {
    return new Date(taken).toLocaleDateString()
}

/**
 * A person enters a reading only where a person is the source: a KPI a task reads is recorded by that task. A KPI
 * with no target takes readings too (#1696): it is signed off once it has a source, so a team can measure a
 * baseline before it sets the target. Whether the KPI is signed off is the API's to say: it refuses a reading for
 * one that is not, and the form shows that refusal.
 */
const canRecord = computed(() => props.readable && !pending.value && props.kpi.sourceKind === 'person')

const recording = ref(false)
const newValue = ref('')
const newDay = ref('')
const saving = ref(false)
const recordError = ref<string | null>(null)

/** Today as the date field holds it (`YYYY-MM-DD`), in the person's own timezone. */
function today(): string {
    const now = new Date()
    return new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0, 10)
}

function startRecording(): void {
    recording.value = true
    newValue.value = ''
    newDay.value = today()
    recordError.value = null
}

/** A reading dated today is taken now; one dated earlier is taken at noon that day, clear of either midnight. */
function takenAt(day: string): string {
    return day === today() ? new Date().toISOString() : new Date(`${day}T12:00:00`).toISOString()
}

async function record(): Promise<void> {
    const value = Number(newValue.value)
    if (String(newValue.value).trim() === '' || !Number.isFinite(value)) {
        recordError.value = 'Enter the number that was read.'
        return
    }
    saving.value = true
    recordError.value = null
    try {
        await api.recordKpiReading(props.kpi.id, { value, takenAt: takenAt(newDay.value || today()) })
        recording.value = false
        // A card handed its readings leaves the read to the list, which reads them again on `recorded`.
        await loadReadings()
        emit('recorded')
    } catch (caught) {
        recordError.value = caught instanceof Error ? `Could not record (${caught.message}).` : 'Could not record.'
    } finally {
        saving.value = false
    }
}
</script>

<template>
  <div class="border-border bg-card rounded-md border p-3 text-sm">
    <div class="flex items-start justify-between gap-2">
      <span class="flex items-center gap-2">
        <ZhKpiLight v-if="light" :rag="light" data-testid="kpi-card-light" />
        <span class="font-medium">{{ kpi.name }}</span>
      </span>
      <span class="flex shrink-0 items-center gap-2">
        <ZhSystemLabel v-if="pending" as="span" tone="system" data-testid="kpi-card-pending"
          >Waiting for sign-off</ZhSystemLabel
        >
        <span
          v-if="kpi.isKey"
          class="text-muted-foreground text-xs uppercase"
          >Key KPI</span
        >
      </span>
    </div>
    <p v-if="pending" class="text-muted-foreground mt-1 text-xs" data-testid="kpi-card-pending-note">
      No run sees this KPI or records a reading for it until a person signs it off.
    </p>
    <p :class="hasTarget ? 'mt-1' : 'text-muted-foreground mt-1 italic'">
      {{ target }}
    </p>
    <p
      v-if="readable && shownReadings"
      data-testid="kpi-card-latest"
      class="mt-1 text-xs"
    >
      <template v-if="latest">
        Latest {{ valueText(latest.value) }}<template v-if="trend"> · {{ trend }}</template> · read
        {{ dayText(latest.takenAt) }} by {{ latest.recordedBy
        }}<template v-if="overdue"> · overdue</template>
      </template>
      <span v-else class="text-muted-foreground italic">No reading yet.</span>
    </p>
    <dl class="text-muted-foreground mt-2 space-y-0.5 text-xs">
      <div v-if="band" class="flex gap-1">
        <dt>Amber band</dt>
        <dd>
          {{ band
          }}<template v-if="kpi.onTargetWorseningRule"
            >, and when moving the wrong way</template
          >
        </dd>
      </div>
      <div v-if="cadence" class="flex gap-1">
        <dt>Read</dt>
        <dd>{{ cadence }}</dd>
      </div>
      <div v-if="source" class="flex gap-1" data-testid="kpi-card-source">
        <dt>Source</dt>
        <dd>
          {{ source.kind }}:
          <RouterLink
            v-if="source.to"
            :to="source.to"
            class="underline-offset-2 hover:underline"
            >{{ source.name }}</RouterLink
          >
          <template v-else>{{ source.name }}</template>
        </dd>
      </div>
    </dl>
    <details
      v-if="readable && ordered.length > 1"
      data-testid="kpi-card-history"
      class="text-muted-foreground mt-2 text-xs"
    >
      <summary class="cursor-pointer">History ({{ ordered.length }} readings)</summary>
      <ul class="mt-1 space-y-0.5">
        <li v-for="reading in ordered" :key="reading.id">
          {{ valueText(reading.value) }} · {{ dayText(reading.takenAt) }} ·
          {{ reading.recordedBy }}
        </li>
      </ul>
    </details>
    <template v-if="canRecord">
      <form
        v-if="recording"
        data-testid="kpi-card-record-form"
        class="mt-2 flex flex-wrap items-end gap-2"
        @submit.prevent="record"
      >
        <label class="block text-xs">
          <span class="text-muted-foreground">Reading{{ kpi.unit ? ` (${kpi.unit})` : '' }}</span>
          <input
            v-model="newValue"
            data-testid="kpi-card-record-value"
            inputmode="decimal"
            class="border-input bg-background mt-1 block w-28 rounded-md border px-2 py-1 text-sm"
          />
        </label>
        <label class="block text-xs">
          <span class="text-muted-foreground">Taken on</span>
          <input
            v-model="newDay"
            data-testid="kpi-card-record-day"
            type="date"
            :max="today()"
            class="border-input bg-background mt-1 block rounded-md border px-2 py-1 text-sm"
          />
        </label>
        <Button type="submit" size="sm" data-testid="kpi-card-record-save" :disabled="saving">Record</Button>
        <Button type="button" size="sm" variant="ghost" @click="recording = false">Cancel</Button>
        <p
          v-if="recordError"
          data-testid="kpi-card-record-error"
          class="text-destructive w-full text-xs"
          role="alert"
        >
          {{ recordError }}
        </p>
      </form>
      <Button
        v-else
        type="button"
        size="sm"
        variant="outline"
        class="mt-2"
        data-testid="kpi-card-record"
        @click="startRecording"
      >
        Record a reading
      </Button>
    </template>
  </div>
</template>
